import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { expenseReimbursementService } from '../services/expenseReimbursementService';
import { formatCurrency } from '../components/CurrencyTextField';
import { formatDateDisplay } from '../utils/dateUtils';
import { useAuth } from '../hooks/useAuth';

const statusColor = (status) => {
  switch (status) {
    case 'approved':
      return 'info';
    case 'paid':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'warning';
  }
};

const sourceLabel = (row) => {
  if (row?.source === 'sales_portal') return 'Sales Portal';
  if (row?.source === 'employee_portal') return 'Employee Portal';
  if (row?.source === 'kiosk') return 'Kiosk';
  return row?.device_name || '—';
};

const eventTypeLabel = (type) => {
  switch (type) {
    case 'submitted':
      return 'Submitted';
    case 'updated':
      return 'Updated';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'reopened':
      return 'Reopened';
    case 'paid':
      return 'Marked paid';
    case 'deleted':
      return 'Deleted';
    default:
      return type || 'Event';
  }
};

const eventChipColor = (type) => {
  switch (type) {
    case 'approved':
    case 'paid':
      return 'success';
    case 'rejected':
    case 'deleted':
      return 'error';
    case 'submitted':
      return 'info';
    case 'reopened':
      return 'warning';
    default:
      return 'default';
  }
};

const formatDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-US', { timeZone: 'America/New_York' });
  } catch {
    return formatDateDisplay(v) || '—';
  }
};

const ExpenseReimbursementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManagerOrAdmin } = useAuth();
  const [expense, setExpense] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editReceiptDate, setEditReceiptDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('pending');

  const loadEvents = useCallback(async (expenseId) => {
    setEventsLoading(true);
    try {
      const res = await expenseReimbursementService.getEvents(expenseId);
      setEvents(res.data || []);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await expenseReimbursementService.getById(id);
      const row = res.data;
      setExpense(row);
      setEditAmount(row.amount != null ? String(row.amount) : '');
      setEditReceiptDate(row.receipt_date ? String(row.receipt_date).slice(0, 10) : '');
      setEditNotes(row.notes || '');
      setEditStatus(row.status || 'pending');
      void loadEvents(row.id);
    } catch (err) {
      setExpense(null);
      setError(err.response?.data?.message || err.message || 'Failed to load expense');
    } finally {
      setLoading(false);
    }
  }, [id, loadEvents]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let revoked = false;
    let objectUrl = '';
    (async () => {
      if (!expense?.id) {
        setImageUrl('');
        return;
      }
      try {
        const response = await axios.get(expenseReimbursementService.getImageUrl(expense.id), {
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(response.data);
        if (!revoked) setImageUrl(objectUrl);
      } catch {
        if (!revoked) setImageUrl('');
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [expense?.id]);

  if (!isManagerOrAdmin()) {
    return (
      <Box>
        <Alert severity="warning">You do not have permission to review expense reimbursements.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!expense) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Expense not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/expense-reimbursements')}>
          Back to list
        </Button>
      </Box>
    );
  }

  const employeeName =
    [expense.first_name, expense.last_name].filter(Boolean).join(' ').trim() || '—';
  const employeeLabel = expense.employee_number
    ? `${employeeName} (#${expense.employee_number})`
    : employeeName;
  const reviewerName = [expense.reviewed_by_first_name, expense.reviewed_by_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await expenseReimbursementService.update(expense.id, {
        status: editStatus,
        amount: editAmount === '' ? null : editAmount,
        receipt_date: editReceiptDate === '' ? null : editReceiptDate,
        notes: editNotes,
      });
      const row = res.data;
      setExpense(row);
      setEditAmount(row.amount != null ? String(row.amount) : '');
      setEditReceiptDate(row.receipt_date ? String(row.receipt_date).slice(0, 10) : '');
      setEditNotes(row.notes || '');
      setEditStatus(row.status || 'pending');
      setSuccess('Expense updated.');
      window.dispatchEvent(new CustomEvent('expense-reimbursements-updated'));
      void loadEvents(row.id);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hr/expense-reimbursements')}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Expense #{expense.id}
        </Typography>
        <Chip label={expense.status} size="small" color={statusColor(expense.status)} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Details
          </Typography>
          <Stack spacing={1.25} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Employee</Typography>
              <Typography variant="body2">{employeeLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Submitted</Typography>
              <Typography variant="body2">
                {expense.submitted_at ? formatDateDisplay(expense.submitted_at) : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Source</Typography>
              <Typography variant="body2">{sourceLabel(expense)}</Typography>
            </Box>
            {expense.device_name && (
              <Box>
                <Typography variant="caption" color="text.secondary">Device</Typography>
                <Typography variant="body2">{expense.device_name}</Typography>
              </Box>
            )}
            {reviewerName && (
              <Box>
                <Typography variant="caption" color="text.secondary">Reviewed by</Typography>
                <Typography variant="body2">
                  {reviewerName}
                  {expense.reviewed_at ? ` · ${formatDateDisplay(expense.reviewed_at)}` : ''}
                </Typography>
              </Box>
            )}
            {expense.paid_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">Paid at</Typography>
                <Typography variant="body2">{formatDateDisplay(expense.paid_at)}</Typography>
              </Box>
            )}
          </Stack>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Update
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Amount ($)"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Receipt date"
            type="date"
            value={editReceiptDate}
            onChange={(e) => setEditReceiptDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Job/Notes"
            multiline
            minRows={3}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              disabled={saving}
              onClick={() => {
                setEditAmount(expense.amount != null ? String(expense.amount) : '');
                setEditReceiptDate(expense.receipt_date ? String(expense.receipt_date).slice(0, 10) : '');
                setEditNotes(expense.notes || '');
                setEditStatus(expense.status || 'pending');
              }}
            >
              Reset
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Current amount: {expense.amount != null ? formatCurrency(expense.amount) : '—'}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Receipt photo
          </Typography>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Receipt"
              sx={{
                width: '100%',
                maxHeight: 560,
                objectFit: 'contain',
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Receipt image unavailable.
            </Typography>
          )}
        </Paper>
      </Box>

      <Paper sx={{ p: 2.5, mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          History
        </Typography>
        {eventsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No history events yet for this expense.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {[...events].reverse().map((ev) => {
              const actor = [ev.actor_first_name, ev.actor_last_name].filter(Boolean).join(' ').trim();
              const actorLabel = actor
                ? (ev.actor_employee_number ? `${actor} (#${ev.actor_employee_number})` : actor)
                : 'System / unknown';
              const bits = [];
              if (ev.amount != null) bits.push(formatCurrency(ev.amount));
              if (ev.previous_amount != null && Number(ev.previous_amount) !== Number(ev.amount)) {
                bits.push(`was ${formatCurrency(ev.previous_amount)}`);
              }
              if (ev.previous_status && ev.previous_status !== ev.status) {
                bits.push(`${ev.previous_status} → ${ev.status || '—'}`);
              } else if (ev.status) {
                bits.push(ev.status);
              }
              if (ev.receipt_date) bits.push(`receipt ${formatDateDisplay(ev.receipt_date)}`);
              if (ev.channel) bits.push(ev.channel.replace(/_/g, ' '));
              return (
                <Box
                  key={ev.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                    gap: 1,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(ev.occurred_at)}
                  </Typography>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                      <Chip
                        size="small"
                        label={eventTypeLabel(ev.event_type)}
                        color={eventChipColor(ev.event_type)}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {actorLabel}
                      </Typography>
                    </Box>
                    {bits.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {bits.join(' · ')}
                      </Typography>
                    )}
                    {ev.notes ? (
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {ev.notes}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ExpenseReimbursementDetail;
